import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DropZone from 'react-dropzone';
import './ff-file-input.scss';
import {
  strictValidObjectWithKeys,
  strictValidArray,
} from '../../../utils/commonUtils';

// import FileUploader from './file-uploader'; // eslint-disable-line
import Alert from '../../--primitives/pr-alert';
// import ErrorPopUp from '../../containers/error-popup';
import ErrorMsg from '../../s3-file-uploader/error-msg-popup';

const initialThanksmodalState = {
  message: '',
  show: false,
};

const initialState = {
  files: [],
  error: null,
  errorModel: initialThanksmodalState,
};

function FfFileInput({
  onChange,
  style,
  showErrorModal,
  onFileUpload,
  uploadMultiple,
  allowedMimeTypes,
  toShowContentIcon,
  toShowContent,
  toShowContentStyle,
  onErrorChange,
  ...otherProps
}) {
  const dropzoneRef = useRef(null);
  const [state, setState] = useState(initialState);

  // Handles on file drop
  function onDrop(acceptedFiles) {
    const { files } = state;
    const { onChangeInfo } = otherProps;
    const rejectedFiles = [];
    // error on uploadMultiple files if uploadMultiple key value is false
    if (acceptedFiles.length > 1 && !uploadMultiple) {
      const errorMsg = 'Uploaded more then one file';
      setState((prev) => ({
        ...prev,
        error: errorMsg,
        errorModel: { message: errorMsg, show: true },
      }));
      return false;
    }
    // Check if file is in allowedMimeTypes, if allowedMimeTypes is not defined or empty ignore it
    const filesToUpload = acceptedFiles.filter((file) => {
      const isValidMimeTypes =
        allowedMimeTypes &&
        Array.isArray(allowedMimeTypes) &&
        !!allowedMimeTypes.length;
      let isFileAllowedFlag = !isValidMimeTypes;
      if (isValidMimeTypes) {
        const type =
          file.type || (file.name && `.${file.name.split('.').pop()}`);
        for (let i = 0; i < allowedMimeTypes.length; i++) {
          if (
            type &&
            type.toLowerCase().includes(allowedMimeTypes[i].toLowerCase())
          ) {
            isFileAllowedFlag = true;
            break;
          }
        }
      }
      const isDuplicate =
        files.length > 0 && files.findIndex((v) => v.name === file.name) >= 0;
      if (isDuplicate) return false;

      if (!isFileAllowedFlag) rejectedFiles.push(file);
      return isFileAllowedFlag;
    });

    // Show error if there are files rejected
    if (rejectedFiles.length && allowedMimeTypes.length) {
      const allowType = allowedMimeTypes.filter(
        (v, k) =>
          v !== 'application/msword' &&
          v !==
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document' &&
          v !== 'application/pdf',
      );

      const lastMimeType = strictValidArray(allowType)
        ? allowType[allowType.length - 1]
        : '';
      const allowedMimeTypeText =
        `${allowType
          .filter((v, k) => k !== allowType.length - 1)
          .map((val) => {
            let v = val;
            if (v.charAt(0) === '.') {
              v = v.substr(1);
            }
            v = v.toUpperCase();
            return v;
          })
          .join(', ')}` +
        ` ${allowType.length > 1 ? 'or' : ''}` +
        ` ${
          lastMimeType && lastMimeType.charAt(0) === '.'
            ? lastMimeType.substr(1).toUpperCase()
            : lastMimeType.toUpperCase()
        }`;
      // Handle error messages
      let errorMsg = '';
      if (onErrorChange && typeof onErrorChange === 'function') {
        const errorInfo = onChangeInfo;
        if (strictValidObjectWithKeys(errorInfo)) {
          errorInfo.error = `Please upload a valid ${allowedMimeTypeText} file`;
          errorMsg = errorInfo.error;
        }
        onErrorChange('uploadFileErr', errorInfo);
      } else {
        errorMsg = `Please upload a valid ${allowedMimeTypeText} file`;
        setState((prev) => ({ ...prev, error: errorMsg }));
      }
      setState((prev) => ({
        ...prev,
        errorModel: { message: errorMsg, show: true },
      }));
    } else if (onErrorChange && typeof onErrorChange === 'function') {
      const errorInfo = onChangeInfo;
      if (strictValidObjectWithKeys(errorInfo)) {
        errorInfo.error = null;
      }
      onErrorChange('uploadFileErr', errorInfo);
    } else {
      setState((prev) => ({ ...prev, error: null }));
    }
    setState((prev) => ({
      ...prev,
      files: uploadMultiple ? files.concat(filesToUpload) : filesToUpload,
    }));
    onFileUpload(uploadMultiple ? files.concat(filesToUpload) : filesToUpload);
    return true;
  }

  return (
    <div className="fullWidth">
      <DropZone
        ref={dropzoneRef}
        multiple={uploadMultiple}
        onDrop={(files) => onDrop(files)}
        style={toShowContentStyle}
      >
        {typeof toShowContent === 'function'
          ? toShowContent(dropzoneRef)
          : toShowContent}
      </DropZone>
      {!!state.error &&
        !(onErrorChange && typeof onErrorChange === 'function') &&
        (showErrorModal ? (
          <ErrorMsg
            message={state.errorModel.message}
            isOpen={state.errorModel.show}
            toggle={() =>
              setState((prev) => ({
                ...prev,
                errorModel: initialThanksmodalState,
              }))
            }
          />
        ) : (
          <Alert size="full" type="error" value={state.error} />
        ))}
    </div>
  );
}

FfFileInput.propTypes = {
  placeholder: PropTypes.string,
  type: PropTypes.string,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  style: PropTypes.shape(PropTypes.object),
  onChange: PropTypes.func,
  toShowContentIcon: PropTypes.bool,
  toShowContent: PropTypes.string,
  toShowContentStyle: PropTypes.shape(PropTypes.object),
  onErrorChange: PropTypes.func,
  onFileUpload: PropTypes.func,
  allowedMimeTypes: PropTypes.shape(PropTypes.array),
  showErrorModal: PropTypes.bool,
  uploadMultiple: PropTypes.bool,
};

FfFileInput.defaultProps = {
  placeholder: '',
  meta: {
    touched: false,
    error: '',
  },
  type: 'text',
  onChange: null,
  onErrorChange: null,
  onFileUpload: null,
  style: {},
  toShowContentIcon: true,
  toShowContent: ' Drag & Drop or Select a file to upload',
  toShowContentStyle: {
    borderWidth: '2px',
    borderColor: '#000',
    borderStyle: 'none',
    borderRadius: '2px',
    width: '100%',
  },
  allowedMimeTypes: [],
  showErrorModal: false,
  uploadMultiple: false,
};

export default FfFileInput;
